import { NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { fromEvent, fromEvent as observableFromEvent, of as observableOf, Subject } from 'rxjs';
import { debounceTime, map, mergeAll, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { DataTableColumn, DataTableColumnGeometryChange } from '../data-table.types';
import { DataTableColumnResizeDirective } from '../directives/data-table-column-resize.directive';

@Component({
  selector: 'rt-data-table-geometry',
  template: `
    <div class="grips">
      <ng-container *ngFor="let col of cols; trackBy: trackByColumn">
        <ng-container *ngIf="col.options.resizable">
          <div #grip class="grip" [attr.data-column-id]="col.id"></div>
        </ng-container>
      </ng-container>
    </div>
    <div #tableContainer>
      <ng-content></ng-content>
    </div>
  `,
  styleUrls: ['./data-table-geometry.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgFor, NgIf],
})
export class DataTableGeometryComponent implements AfterViewInit, OnChanges, OnDestroy {
  /**
   * Width (in pixels) of moveable column grip
   * NOTE remember to change it in .less file as well when changing this value
   */
  public readonly COLUMN_GRIP_WIDTH: number = 5;

  @Input() public autoFit: boolean;
  @Input() public cols: DataTableColumn[];

  @Output() public geometryChange: EventEmitter<DataTableColumnGeometryChange> = new EventEmitter();

  /**
   * Container of table, it is used to measure table geometry
   * Note: DataTableGeometry will directly touch this particular DOMNode (read)
   */
  @ViewChild('tableContainer', { static: true }) private tableContainer: ElementRef;
  private tableElement: HTMLElement;

  @ViewChildren('grip') private grips: QueryList<ElementRef>;

  @ContentChildren(DataTableColumnResizeDirective, { descendants: true })
  private columnDirectives: QueryList<DataTableColumnResizeDirective>;

  private ngOnDestroy$ = new Subject<void>();

  constructor(private elRef: ElementRef, private renderer: Renderer2) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.autoFit && !changes.autoFit.firstChange) {
      this.autoFit ? this.enableAutoFit() : this.disableAutoFit();
    }

    if (changes.cols && !changes.cols.firstChange) {
      requestAnimationFrame(() => {
        this.updateColumnsWidths();
        this.updateColumnsGrips();
      });
    }
  }

  public ngAfterViewInit(): void {
    this.tableElement = this.tableContainer.nativeElement.querySelector('table');

    this.autoFit ? this.enableAutoFit() : this.disableAutoFit();

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.updateColumnsGrips());

    this.grips.changes
      .pipe(
        takeUntil(this.ngOnDestroy$.asObservable()),
        debounceTime(10), // sometimes changes are applied one-after-another, lets debounce it
        startWith(this.grips.toArray()),
        switchMap((grips: any) =>
          observableOf(
            ...grips.map((grip: any) => {
              const column = this.cols.find(col => col.id === grip.nativeElement.dataset.columnId);
              return observableFromEvent(grip.nativeElement, 'mousedown').pipe(
                map(evt => ({
                  evt,
                  column,
                })),
              );
            }),
          ).pipe(mergeAll()),
        ),
      )
      .subscribe(({ evt, column }) => this.handleColumnGripDragStart(column, evt));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public enableTableInteraction(): void {
    this.renderer.removeStyle(this.tableElement, 'pointerEvents');
    this.renderer.removeStyle(this.tableElement, 'userSelect');
  }

  public disableTableInteraction(): void {
    this.renderer.setStyle(this.tableElement, 'pointerEvents', 'none');
    this.renderer.setStyle(this.tableElement, 'userSelect', 'none');
  }

  public trackByColumn<T extends { id: string }>(index: number, { id }: T): string {
    return id;
  }

  private ignoreAutoFit(): void {
    this.columnDirectives
      .map(columnDirective => {
        const column = this.cols.find(col => col.id === columnDirective.columnId);
        if (column) {
          const width = columnDirective.width;
          return { columnDirective, width };
        }

        return undefined;
      })
      .filter(x => !!x)
      .forEach(({ columnDirective, width }: any) => {
        columnDirective.setWidth(`${width}px`, `${width}px`, `${width}px`);
      });
    this.renderer.removeStyle(this.tableElement, 'width');
  }

  private enableAutoFit(): void {
    this.renderer.setStyle(this.tableElement, 'width', '100%');
    requestAnimationFrame(() => {
      this.updateColumnsWidths();
      this.updateColumnsGrips();
    });
  }

  private disableAutoFit(): void {
    requestAnimationFrame(() => {
      this.updateColumnsWidths();
      this.updateColumnsGrips();
    });
    this.renderer.removeStyle(this.tableElement, 'width');
  }

  private updateColumnsWidths(): void {
    this.columnDirectives.forEach(columnDirective => {
      const column = this.cols.find(col => col.id === columnDirective.columnId);
      if (column) {
        const { width, minWidth, maxWidth } = column.geometry;
        if (this.autoFit) {
          const autoFitWidth = column.geometry[column.geometry.autoFitProp!]
            ? `${column.geometry[column.geometry.autoFitProp!]}px`
            : 'auto';

          columnDirective.setWidth(
            column.options.resizable ? autoFitWidth : `${width}px`,
            minWidth ? `${minWidth}px` : null,
            maxWidth && maxWidth !== Number.POSITIVE_INFINITY ? `${maxWidth}px` : null,
          );
        } else {
          columnDirective.setWidth(`${width}px`, `${width}px`, `${width}px`);
        }
      }
    });
  }

  private updateColumnsGrips(): void {
    this.columnDirectives.forEach((columnDirective, i) => {
      const grip = this.grips.find(
        g => g.nativeElement.dataset.columnId === columnDirective.columnId,
      );
      if (grip) {
        this.updateColumnGripPosition(
          grip.nativeElement,
          columnDirective.offsetRight,
          this.columnDirectives.length === i + 1,
        );
      }
    });
  }

  private updateColumnGripPosition(
    gripElement: HTMLElement,
    offset: number,
    isLast: boolean,
  ): void {
    let gripOffset = this.COLUMN_GRIP_WIDTH / 2;
    if (isLast) {
      gripOffset = this.COLUMN_GRIP_WIDTH - 1;
    }
    this.renderer.setStyle(gripElement, 'left', `${offset - gripOffset}px`);
  }

  private handleColumnGripDragStart(column: DataTableColumn, mouseDownEvt: MouseEvent): void {
    mouseDownEvt.preventDefault();

    if (this.autoFit) {
      this.ignoreAutoFit();
    }

    this.disableTableInteraction();

    const mouseUp$ = observableFromEvent(document, 'mouseup');
    const mouseMove$ = observableFromEvent(document, 'mousemove');

    const columnDirective = this.columnDirectives.find(
      colDirective => colDirective.columnId === column.id,
    );
    const startX = mouseDownEvt.pageX;
    const initialWidth = columnDirective!.width;
    let tick = false;

    mouseMove$.pipe(takeUntil(mouseUp$)).subscribe({
      next: (mouseMoveEvt: MouseEvent) => {
        if (!tick) {
          tick = true;
          requestAnimationFrame(() => {
            const { minWidth, maxWidth } = column.geometry;
            // NOTE It cannot be equal to 0 because it causes some render issues, so the minimum value is 1
            const width = Math.min(
              Math.max(initialWidth + (mouseMoveEvt.pageX - startX), minWidth || 1),
              maxWidth || Number.POSITIVE_INFINITY,
            );
            columnDirective!.setWidth(`${width}px`, `${width}px`, `${width}px`);
            this.updateColumnsGrips();
            tick = false;
          });
        }
        mouseMoveEvt.preventDefault();
      },
      complete: () => this.handleColumnGripDragEnd(),
    });
  }

  private handleColumnGripDragEnd(): void {
    this.enableTableInteraction();
    this.geometryChange.emit({
      autofit: false,
      columns: this.columnDirectives.map(columnDirective => {
        const column = this.cols.find(col => col.id === columnDirective.columnId);
        return {
          id: column!.id,
          geometry: {
            ...column!.geometry,
            width: columnDirective.width,
          },
        };
      }),
    });
  }
}
